"use client";

import {
  AFTER_LOGIN_REDIRECT,
  NO_ORGANIZATION_REDIRECT,
  POST_LOGIN_REDIRECT_KEY,
  SITE_CREATION_HOSTS,
  USER_LANG,
  USER_TOKEN_PERSIST,
  isBrowser
} from "@constants/global";
import GroowLoader from "components/Loader/GroowLoader";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import setLanguage from "next-translate/setLanguage";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from "routes/routes";
import useAppStore from "stores/useAppStore";
import { useUserStore } from "stores/user/userStore";
import {
  hasFirstLoginInLocation,
  isSafeInternalRedirect,
  sanitizeInternalRedirect,
  stashLegacyFirstLoginQuery,
} from "utils/safe-navigation";

// SiteLoader is a wrapper component that manages authentication, onboarding, and route redirection logic for the app.
// It ensures users are redirected to the correct page based on their authentication and onboarding state.

const SiteLoader = ({ children }) => {
  // State to control the display of the loader
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [storeHydrated, setStoreHydrated] = useState(
    () => useUserStore.persist?.hasHydrated?.() ?? true
  );
  const { lang } = useTranslation();

  const router = useRouter();

  // Get user and onboarding data from the global store
  const { user } = useUserStoreContext((store) => ({
    user: store.user,
    logout: store.logout
  }));

  // App initialization state
  const { isInitialized } = useAppStore();

  // Helper to check if the current route is public
  const isPublicRoute = useCallback(
    () =>
      Object.values(PUBLIC_ROUTES)
        .map((route) => route.path)
        .includes(router.pathname),
    [router.pathname]
  );

  // Helper to check if the current route is the login page
  const isLoginRoute = router.pathname === PUBLIC_ROUTES.login.path;

  useEffect(() => {
    const unsub = useUserStore.persist.onFinishHydration(() => {
      setStoreHydrated(true);
    });
    setStoreHydrated(useUserStore.persist.hasHydrated());
    return unsub;
  }, []);

  // Site creation hosts: allow public auth flows; otherwise force org creation.
  // Must run in an effect — never call router.push during render.
  useEffect(() => {
    if (!isBrowser || !SITE_CREATION_HOSTS.includes(window.location.host)) {
      return;
    }

    const allowedOnCreationHost: string[] = [
      PUBLIC_ROUTES.login.path,
      PUBLIC_ROUTES.logout.path,
      PUBLIC_ROUTES.register.path,
      PUBLIC_ROUTES.recoveryPasswordSendEmail.path,
      PUBLIC_ROUTES.recoveryPasswordSetNewPass.path,
      PUBLIC_ROUTES.linkedin.path,
      PUBLIC_ROUTES.invitation.path,
      PUBLIC_ROUTES.newOrganization.path,
      PUBLIC_ROUTES.vendorSelector.path
    ];

    if (allowedOnCreationHost.includes(router.pathname)) {
      return;
    }

    router
      .push(PUBLIC_ROUTES.newOrganization.path)
      .then(() => setShowLoader(false));
  }, [router.pathname, isInitialized, router]);

  useEffect(() => {
    // Wait until the app is initialized and the persisted user store has rehydrated
    if (!isInitialized || !storeHydrated) return;

    // Check if we're in the middle of Microsoft authentication
    // This prevents SiteLoader from interfering with the Microsoft login flow
    const urlParams = new URLSearchParams(window.location.search);
    const isMicrosoftAuth =
      urlParams.has("code") ||
      urlParams.has("state") ||
      urlParams.has("session_state") ||
      urlParams.has("client_info") ||
      (isBrowser &&
        sessionStorage.getItem("microsoftAuthInProgress") === "true");

    if (isMicrosoftAuth && isLoginRoute) {
      // Shorter delay since login forms now handle immediate detection
      const authTimeout = setTimeout(() => {
        setShowLoader(false);
      }, 1000);

      return () => clearTimeout(authTimeout);
    }

    // Case: user is not authenticated
    if (!user) {
      if (!isPublicRoute()) {
        // Guard against a race: the store flips isInitialized synchronously but the
        // user object hydrates a tick later. If a session token exists, the user is
        // still loading — render the app so AppNavbar can fetch the profile.
        // A genuinely invalid token is handled by Apollo's onError (refresh/logout).
        if (isBrowser && localStorage.getItem(USER_TOKEN_PERSIST)) {
          setShowLoader(false);
          return;
        }

        // Handle special case for first login (legacy query or hash).
        // Never put the JWT back into a `loginsource` query param.
        if (
          router.asPath.includes("firstLogin") ||
          hasFirstLoginInLocation()
        ) {
          stashLegacyFirstLoginQuery();
          router
            .push(PUBLIC_ROUTES.login.path)
            .then(() => setShowLoader(false));
        } else {
          // Redirect to login, preserving a *safe* internal path as redirect
          // (also stash in sessionStorage for Microsoft SSO round-trips).
          const safeRedirect = isSafeInternalRedirect(router.asPath)
            ? router.asPath
            : null;
          if (isBrowser && safeRedirect) {
            sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, safeRedirect);
          }
          router
            .push({
              pathname: PUBLIC_ROUTES.login.path,
              ...(safeRedirect
                ? { query: { redirect: safeRedirect } }
                : {}),
            })
            .then(() => setShowLoader(false));
        }
      } else {
        // If on a public route, just hide the loader
        setShowLoader(false);
      }
      // Exit early, no further logic needed for unauthenticated users
      return;
    }

    // Case: user is authenticated

    // Check if we're still processing Microsoft authentication
    // This prevents premature redirects during Microsoft login completion
    if (isMicrosoftAuth && isLoginRoute) {
      setShowLoader(false);
      return;
    }

    // Set the user's preferred language if different from current
    if (user.lang !== lang) {
      localStorage.setItem(USER_LANG, user.lang ?? "en");
      setLanguage(user.lang ?? "en", false);
    }

    // Case: user has vendors (i.e., is fully onboarded)
    if (user.vendorList?.length > 0) {
      // If on root, no-organization, or login, determine where to redirect
      if (
        router.pathname === "/" ||
        router.pathname === "/no-organization" ||
        isLoginRoute
      ) {
        // Check for Microsoft auth parameters before redirecting
        if (isMicrosoftAuth) {
          setShowLoader(false);
          return;
        }

        // Prefer the redirect query (form login), falling back to the saved
        // destination (survives the Microsoft SSO round-trip that drops the query).
        const storedRedirect = isBrowser
          ? sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
          : null;
        const redirectTarget = sanitizeInternalRedirect(
          (router.query?.redirect as string) || storedRedirect,
          ""
        );

        if (redirectTarget) {
          if (isBrowser) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);

          const [pathname, search] = redirectTarget.split("?");
          const q = Object.fromEntries(new URLSearchParams(search));

          router
            .push({
              pathname: pathname,
              query: search ? q : {}
            })
            .then(() => setShowLoader(false));
        } else {
          if (isBrowser) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
          router.push(AFTER_LOGIN_REDIRECT).then(() => setShowLoader(false));
        }
      } else {
        // Already on a private route: clear any stale saved redirect and show it.
        if (isBrowser) sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
        setShowLoader(false);
      }
    }
    // Case: user does not have vendors (not fully onboarded)
    else if (
      router.pathname !== "/no-organization" &&
      router.pathname !== "/new-organization" &&
      router.pathname !== "/inv"
    ) {
      // Redirect to the no-organization page
      router.push(NO_ORGANIZATION_REDIRECT).then(() => setShowLoader(false));
    } else {
      setShowLoader(false);
    }
  }, [user, router.pathname, isInitialized, storeHydrated]);

  // Exclude certain public pages from showing the loader for SEO and UX reasons
  if (
    showLoader &&
    router.pathname !== PUBLIC_ROUTES.landingPublic.path &&
    router.pathname !== PUBLIC_ROUTES.activityPage.path &&
    router.pathname !== PUBLIC_ROUTES.jobyfine.path &&
    router.pathname !== PUBLIC_ROUTES.myCertificate.path &&
    router.pathname !== PUBLIC_ROUTES.jobs.path &&
    router.pathname !== PRIVATE_ROUTES.thread.path
  ) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-white dark:bg-black">
        <GroowLoader size={300} />
      </div>
    );
  }

  // Render children if no loader is needed
  return <>{children}</>;
};

export default SiteLoader;
